import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import { useI18n } from '../../i18n/I18nContext';

export const AI_TOPIC_MAX = 300;
export const AI_TYPES = ['STORY_ADVENTURE', 'PUZZLE_MASTER'];

/** Topic, kind and lesson for POST /lessons/:lessonId/games/generate. */
export default function AiGameDialog({ open, lessons, moduleNameOf, lessonId, saving, onClose, onStart }) {
  const { t } = useI18n();
  const g = (key) => `gameAuthoring.games.${key}`;
  const [form, setForm] = useState({ lessonId: '', gameType: 'PUZZLE_MASTER', topic: '' });

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        topic: '',
        lessonId: lessonId || (lessons.length === 1 ? lessons[0].id : ''),
      }));
    }
  }, [open, lessonId, lessons]);

  const topic = form.topic.trim();
  const canStart = !saving && form.lessonId && topic && form.topic.length <= AI_TOPIC_MAX;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t(g('aiTitle'))}
      as="form"
      onSubmit={() => { if (canStart) onStart({ lessonId: form.lessonId, gameType: form.gameType, topic }); }}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('courseAssess.games.cancel')}</button>
          <button type="submit" className={`ds-btn ds-btn-primary ${saving ? 'is-loading' : ''}`} disabled={!canStart}>
            <Sparkles aria-hidden="true" />
            {t(g('aiStart'))}
          </button>
        </>
      )}
    >
      <div className="cg-dialog-fields">
        <p className="cg-dialog-intro">{t(g('aiIntro'))}</p>
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-ai-lesson">{t('courseAssess.games.lessonLabel')} <em className="cg-required">*</em></label>
          <Dropdown
            id="cg-ai-lesson"
            value={form.lessonId}
            onChange={(val) => setForm((f) => ({ ...f, lessonId: val }))}
            options={lessons.map((lesson, idx) => {
              const moduleName = moduleNameOf(lesson.moduleId);
              return { value: lesson.id, label: `${idx + 1}. ${lesson.title}${moduleName ? ` · ${moduleName}` : ''}` };
            })}
            placeholder={t('courseAssess.games.selectLessonPlaceholder')}
            ariaLabel={t('courseAssess.games.lessonLabel')}
          />
        </div>
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-ai-type">{t('courseAssess.games.gameTypeLabel')}</label>
          <Dropdown
            id="cg-ai-type"
            value={form.gameType}
            onChange={(val) => setForm((f) => ({ ...f, gameType: val }))}
            options={AI_TYPES.map((type) => ({ value: type, label: t(`courseAssess.games.gameType.${type}`) }))}
            ariaLabel={t('courseAssess.games.gameTypeLabel')}
          />
        </div>
        <div className="cg-form-field">
          <label className="cg-label" htmlFor="cg-ai-topic">{t(g('aiTopicLabel'))} <em className="cg-required">*</em></label>
          <textarea
            id="cg-ai-topic"
            className="cg-textarea"
            rows={3}
            maxLength={AI_TOPIC_MAX}
            data-autofocus
            placeholder={t(g('aiTopicPlaceholder'))}
            value={form.topic}
            onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
            disabled={saving}
          />
          <span className="cg-counter" aria-live="polite">{t(g('aiTopicCount'), { count: form.topic.length })}</span>
        </div>
      </div>
    </Modal>
  );
}
